import React, { useMemo, useState } from 'react';
import Icon from '../../../Components/Icons/Icon';
import { statuses, useScheduleSuggestion } from '../../../Data/Jobs';
import { classNames, dateToString, weeksInYear, weekString, durationToSeconds, ltrim } from '../../../Services/helpers';
import Loading, { PageLoading } from '../../../Components/Loading';
import RelativeDate from '../../../Components/Formatting/RelativeDate';
import UserName from '../../../Components/Formatting/UserName';
import { usePreference } from '../../../Data/Preference';
import AlertBadge from '../../../Components/Icons/AlertBadge';
import Money from '../../../Components/Formatting/Money';
import Schedule from '../Widgets/ScheduleOverview/Schedule';
import Tile from '../../../Components/Containers/Tile';

// Date status constants
const DATE_STATUS = {
    EMPTY: '-',
    PLANNED: 'Planned',
    IN_PROGRESS: 'In Progress',
    COMPLETE: 'Complete',
    INCOMPLETE: 'Incomplete',
    SUGGESTED: 'Suggested'
};

const DailyTracker = () => {
    const thisWeek = weekString();
    const [week, setWeek] = usePreference('dailyTracker:week', weekString(), true);
    // Don't include the week string for the current week, so we can preload it
    const {dates, isLoading} = useScheduleSuggestion(week === thisWeek ? null : week, true);

    const prevWeek = () => {
        let [y, w] = week.split('-W').map(Number);
        w--;
        if (w < 1) {
            y--;
            w = 52;
        }
        setWeek(`${y}-W${w}`);
    }
    const nextWeek = () => {
        let [y, w] = week.split('-W').map(Number);
        w++;
        if (w > weeksInYear(y)) {
            y++;
            w = 1;
        }
        setWeek(`${y}-W${w}`);
    }

    return <Tile title="Daily tracker"
        icon="CircleCheck"
        options={<div className="flex flex-row items-center gap-2 text-muted text-sm">
            <Icon icon="LeftChevron" size="0.5x" onClick={prevWeek} />
            <DateNav {...{dates, isLoading}} />
            <Icon icon="RightChevron" size="0.5x" onClick={nextWeek} />
        </div>}
        flush
    >
        {dates ? <Inner {...{dates}} /> : <PageLoading />}
    </Tile>
};

const DateNav = ({dates, isLoading}) => {
    if (isLoading || !dates) {
        return <Loading />;
    }
    const ds = Object.keys(dates).sort();
    const from = ltrim(ds[0].split('-')[2], '0');

    const last = ds[ds.length - 1];
    const month = new Date(last).toLocaleString('en-GB', { month: 'short' });

    const to = ltrim(last.split('-')[2], '0')+' '+month;

    return <div className="flex flex-row items-center gap-2">
        {`${from} - ${to}`}
    </div>;
}

const Inner = ({dates}) => {
    const ds = Object.keys(dates);

    const maxDuration = useMemo(() => {
        return calculateMaxScheduledDuration(dates);
    }, [dates]);

    return <div className="flex flex-col gap-2">
        {ds.map(d => <div key={d} className="p-2 surface-1 rounded-lg surface-outline">
            <DateRow {...{date: d, users: dates[d], maxDuration}} />
        </div>)}
    </div>;
};

const DateRow = ({date, users, maxDuration}) => {
    const today = dateToString(new Date());
    const [expanded, setExpanded] = useState(false);

    // Calculate date status, job counts, and totals
    const { dateStatus, totalJobs, doneJobs, totalCost } = useMemo(() => {
        let totalJobs = 0;
        let doneJobs = 0;
        let totalCost = 0;
        let hasSuggestedJobs = false;

        // Collect all jobs and count done/total, and calculate total cost
        Object.values(users).forEach(userData => {
            hasSuggestedJobs = hasSuggestedJobs || userData?.suggested?.jobs?.length > 0 || userData?.suggested?.rounds?.length > 0;
            const jobsByStatus = getScheduledJobsByStatus(userData);
            Object.entries(jobsByStatus).forEach(([status, jobs]) => {
                const statusConfig = statuses[status];
                jobs.forEach(() => {
                    totalJobs++;
                    if (statusConfig?.considerDone) {
                        doneJobs++;
                    }
                });
            });

            // Calculate total cost for this user
            const { totalCost: userTotalCost } = calculateScheduledTotals(userData);
            totalCost += userTotalCost;
        });

        // Determine status based on conditions
        let dateStatus;
        if (totalJobs === 0) {
            dateStatus = hasSuggestedJobs ? DATE_STATUS.SUGGESTED : DATE_STATUS.EMPTY;
        } else if (date > today) {
            dateStatus = DATE_STATUS.PLANNED;
        } else if (date === today) {
            dateStatus = doneJobs < totalJobs ? DATE_STATUS.IN_PROGRESS : DATE_STATUS.COMPLETE;
        } else { // date < today
            dateStatus = doneJobs === totalJobs ? DATE_STATUS.COMPLETE : DATE_STATUS.INCOMPLETE;
        }

        return { dateStatus, totalJobs, doneJobs, totalCost, hasSuggestedJobs };
    }, [date, users, today]);

    const statusConfig = getDateStatusConfig(dateStatus);

    return <div className="flex flex-row items-start justify-between gap-4">
        <div className="">
            <AlertBadge
                icon={statusConfig.icon}
                size="large"
                theme={statusConfig.theme}
            />
        </div>
        <div className="flex flex-col gap-2 w-full">
            <div className="flex flex-row items-center justify-between gap-2 w-full" onClick={() => setExpanded(!expanded)}>
                <p className={classNames('font-bold grow', date < today && 'text-muted')}>
                    <RelativeDate date={date} />
                    <br />
                    <span className="text-muted text-sm font-normal">
                        {totalJobs > 0 && `${doneJobs} of ${totalJobs} jobs done`}
                        {totalJobs === 0 && 'No jobs scheduled'}
                    </span>
                </p>
                <div className="shrink-0 flex flex-col items-end">
                    <p className={classNames('text-sm font-bold', statusConfig.textColor)}>
                        {dateStatus}
                    </p>
                    <p className="text-sm text-muted">
                        <Money pence={totalCost} />
                    </p>
                </div>
                <div className="shrink">
                    <Icon icon={expanded ? 'UpChevron' : 'DownChevron'} size="1x" className="ml-2" />
                </div>
            </div>
            <div className={classNames('flex flex-col gap-2 transition-all duration-300 h-auto overflow-hidden', expanded ? 'max-h-fit' : 'max-h-0')}>
                {Object.keys(users).map(userId =>
                    <UserRow
                        key={userId}
                        date={date}
                        userId={userId}
                        userData={users[userId]}
                        maxDuration={maxDuration}
                    />
                )}
            </div>
        </div>
    </div>;
}

const UserRow = ({date, userId, userData, maxDuration}) => {
    const jobsByStatus = useMemo(() => getScheduledJobsByStatus(userData), [userData]);
    const [scheduling, setScheduling] = useState(false);

    // Calculate done vs total counts
    const jobCounts = useMemo(() => {
        let done = 0;
        let total = 0;

        Object.entries(jobsByStatus).forEach(([status, jobs]) => {
            const statusConfig = statuses[status];
            total += jobs.length;
            if (statusConfig?.considerDone) {
                done += jobs.length;
            }
        });

        return { done, total };
    }, [jobsByStatus]);

    // Sort statuses by total duration (most to least)
    const sortedStatuses = useMemo(() => {
        return Object.entries(jobsByStatus)
            .map(([status, jobs]) => ({
                status,
                jobs,
                totalDuration: jobs.reduce((sum, job) => sum + durationToSeconds(job.duration ?? '00:15:00'), 0)
            }))
            .filter(item => item.jobs.length > 0)
            .sort((a, b) => b.totalDuration - a.totalDuration);
    }, [jobsByStatus]);

    const hasSuggestedJobs = userData?.suggested?.jobs?.length > 0 || userData?.suggested?.rounds?.length > 0;
    const showSuggestedJobs = hasSuggestedJobs && sortedStatuses.length === 0;

    return <div className="flex flex-row items-center gap-2">
        <div className="w-16 flex flex-row items-center justify-between text-muted text-sm text-center">
            <span>
                {userId === 'unassigned' ? '-' : <UserName userId={userId} you={false} initials />}
            </span>
            <span>
                ({jobCounts.done}/{jobCounts.total})
            </span>
        </div>
        <div className="grow">
            <div className={classNames('w-full rounded-full overflow-hidden', !showSuggestedJobs && 'h-2')}>
                {showSuggestedJobs && <p
                    onClick={() => setScheduling(true)}
                    className="text-xm link"
                >
                    Suggested Jobs
                </p>}
                {(sortedStatuses.length > 0) && (
                    sortedStatuses.map(({status, totalDuration}, index) => {
                        const statusConfig = statuses[status];
                        const segmentWidth = (totalDuration / maxDuration) * 100;

                        return (
                            <div
                                key={status}
                                className={classNames(
                                    'h-full inline-block',
                                    statusConfig?.bgColor,
                                    index === sortedStatuses.length - 1 && 'rounded-r-full'
                                )}
                                style={{ width: `${segmentWidth}%` }}
                            >&nbsp;</div>
                        );
                    })
                )}
                {!showSuggestedJobs && (
                    <div className="h-full w-2 bg-gray-200 rounded-full" />
                )}
            </div>
        </div>
        <Schedule
            isOpen={scheduling}
            setIsOpen={setScheduling}
            date={date}
            assignTo={userId}
            rounds={userData?.suggested?.rounds ?? []}
            jobs={userData?.suggested?.jobs ?? []}
            onScheduled={() => setScheduling(false)}
            onClose={() => setScheduling(false)}
        />
    </div>;
};

// Helper functions
const calculateScheduledTotals = (userData) => {
    let totalTime = 0;
    let totalCost = 0;

    // Sum durations and costs from scheduled rounds
    userData.scheduled?.rounds?.forEach(round => {
        round.jobs?.forEach(job => {
            totalTime += durationToSeconds(job.duration ?? '00:15:00');
            const jobCost = parseInt(job?.cost ?? 0);
            const adjustment = parseInt(job?.itemisationAdjustment?.amount ?? 0);
            totalCost += jobCost + adjustment;
        });
    });

    // Sum durations and costs from scheduled jobs (not in rounds)
    userData.scheduled?.jobs?.forEach(job => {
        totalTime += durationToSeconds(job.duration ?? '00:15:00');
        const jobCost = parseInt(job?.cost ?? 0);
        const adjustment = parseInt(job?.itemisationAdjustment?.amount ?? 0);
        totalCost += jobCost + adjustment;
    });

    return { totalTime, totalCost };
};

const calculateScheduledDuration = (userData) => {
    const { totalTime } = calculateScheduledTotals(userData);
    return totalTime;
};

const getScheduledJobsByStatus = (userData) => {
    const jobsByStatus = {};

    // Get jobs from scheduled rounds
    userData.scheduled?.rounds?.forEach(round => {
        round.jobs?.forEach(job => {
            const status = job.status || 'Unknown';
            if (!jobsByStatus[status]) {
                jobsByStatus[status] = [];
            }
            jobsByStatus[status].push(job);
        });
    });

    // Get jobs from scheduled jobs (not in rounds)
    userData.scheduled?.jobs?.forEach(job => {
        const status = job.status || 'Unknown';
        if (!jobsByStatus[status]) {
            jobsByStatus[status] = [];
        }
        jobsByStatus[status].push(job);
    });

    return jobsByStatus;
};

const calculateMaxScheduledDuration = (dates) => {
    let maxTotalSeconds = 0;

    Object.values(dates).forEach(dayData => {
        Object.values(dayData).forEach(userData => {
            const dayTotalSeconds = calculateScheduledDuration(userData);
            maxTotalSeconds = Math.max(maxTotalSeconds, dayTotalSeconds);
        });
    });

    return maxTotalSeconds;
};

const getDateStatusConfig = (dateStatus) => {
    const configMap = {
        [DATE_STATUS.COMPLETE]: {
            theme: 'success',
            icon: 'Check',
            textColor: 'text-green-600 fill-green-600',
            bgColor: 'bg-green-100'
        },
        [DATE_STATUS.IN_PROGRESS]: {
            theme: 'primary',
            icon: 'Play',
            textColor: 'text-blue-600 fill-blue-600',
            bgColor: 'bg-blue-100'
        },
        [DATE_STATUS.PLANNED]: {
            theme: 'info',
            icon: 'CalendarClock',
            textColor: 'text-gray-600 fill-gray-600',
            bgColor: 'bg-gray-100'
        },
        [DATE_STATUS.INCOMPLETE]: {
            theme: 'danger',
            icon: 'TriangleExclamation',
            textColor: 'text-red-600 fill-red-600',
            bgColor: 'bg-red-100'
        },
        [DATE_STATUS.EMPTY]: {
            theme: 'secondary',
            icon: 'Circle',
            textColor: 'text-muted fill-muted',
            bgColor: 'bg-gray-50'
        },
        [DATE_STATUS.SUGGESTED]: {
            theme: 'warning',
            icon: 'LightbulbOn',
            textColor: 'text-yellow-600 fill-yellow-600',
            bgColor: 'bg-yellow-100'
        }
    };

    return configMap[dateStatus] ?? configMap[DATE_STATUS.EMPTY];
};

export default DailyTracker;
